import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';
import voiceStreamingService from '../services/voiceStreamingService';
import PersonalityTraits from './PersonalityTraits';
import { useToast } from './Toast';

const VoiceSelection = ({ phoneNumber, onVoiceSelected, currentVoiceSettings, cachedVoices, cachedBusinessConfig, adminBusinessId }) => {
  const { updateCache } = useDataCache();
  const [voices, setVoices] = useState(cachedVoices || { male: [], female: [], neutral: [] });
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  const [selectedGender, setSelectedGender] = useState(() => {
    return localStorage.getItem('selectedGender') || 'female';
  });
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const saved = localStorage.getItem('selectedVoice');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(!cachedVoices);
  const [settingsLoaded, setSettingsLoaded] = useState(!!cachedBusinessConfig);
  const [streamingEnabled, setStreamingEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [personalityTraits, setPersonalityTraits] = useState({
    formality: 'Strictly Professional',
    verbosity: 'Concise',
    empathy: 'Polite and Neutral',
    proactivity: 'Offers Clarifications',
    humor: 'Completely Serious'
  });
  const [selectedScenario, setSelectedScenario] = useState('greeting');
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();

  const scenarios = [
    { key: 'greeting', label: 'Greeting'},
    { key: 'hours', label: 'Business Hours'},
    { key: 'manager', label: 'Speak to Manager'},
    { key: 'company', label: 'Tell me about your company'},
    { key: 'farewell', label: 'Farewell'}
  ];

  useEffect(() => {
    if (cachedVoices) {
      setVoices(cachedVoices);
      setLoading(false);
    }
    if (cachedBusinessConfig) {
      console.log('[VoiceSelection] Using cached business config:', {
        business_name: cachedBusinessConfig.business_name,
        voice_id: cachedBusinessConfig.voice_settings?.voice_id
      });
      setSettingsLoaded(true);
    }
  }, [cachedVoices, cachedBusinessConfig]);

  useEffect(() => {
    const initializeComponent = async () => {
      if (!cachedVoices) {
        await loadAvailableVoices();
      }
      if (cachedBusinessConfig) {
        loadExistingSettingsFromCache(cachedBusinessConfig);
      } else {
        await loadExistingSettings();
      }
      if (user) {
        initializeStreaming();
      }
    };
    initializeComponent();
  }, [user]);
  
  // Expose save function to Setup.js
  useEffect(() => {
    window.saveVoiceSelection = async () => {
      try {
        await saveVoiceSelection();
        return true;
      } catch (error) {
        console.error('❌ VoiceSelection: Save failed:', error);
        setError('Failed to save voice settings. Please try again.');
        return false;
      }
    };
    
    return () => {
      delete window.saveVoiceSelection;
    };
  }, [selectedVoice, personalityTraits]);
  
  const loadExistingSettingsFromCache = (config) => {
    console.log('[VoiceSelection] loadExistingSettingsFromCache called:', {
      hasConfig: !!config,
      business_name: config?.business_name,
      voice_id: config?.voice_settings?.voice_id,
      isAdminMode: !!adminBusinessId
    });
    setHasLoadedSettings(true);
    try {
      let voiceFound = false;
      
      if (config) {
        if (config.voice_settings && Object.keys(voices).length > 0) {
          const voiceId = config.voice_settings.voice_id;
          for (const [gender, voiceList] of Object.entries(voices)) {
            const matchingVoice = voiceList.find(voice => voice.voice_id === voiceId);
            if (matchingVoice) {
              setSelectedGender(gender);
              setSelectedVoice(matchingVoice);
              localStorage.setItem('selectedGender', gender);
              localStorage.setItem('selectedVoice', JSON.stringify(matchingVoice));
              voiceFound = true;
              break;
            }
          }
        }
        
        if (config.formality || config.verbosity || config.empathy || config.proactivity || config.humor) {
          setPersonalityTraits({
            formality: config.formality || 'Business Casual',
            verbosity: config.verbosity || 'Balanced',
            empathy: config.empathy || 'Warm and Understanding',
            proactivity: config.proactivity || 'Suggestive',
            humor: config.humor || 'Completely Serious'
          });
        }
      }
      
      if (!voiceFound && Object.keys(voices).length > 0) {
        const defaultGender = 'female';
        if (voices[defaultGender]?.length > 0) {
          const defaultVoice = voices[defaultGender][0];
          setSelectedGender(defaultGender);
          setSelectedVoice(defaultVoice);
          localStorage.setItem('selectedGender', defaultGender);
          localStorage.setItem('selectedVoice', JSON.stringify(defaultVoice));
        }
      }
    } catch (error) {
      console.warn('Could not load cached voice settings:', error);
    }
  };

  const loadExistingSettings = async () => {
    if (!user?.attributes?.sub) return;
    console.log('[VoiceSelection] loadExistingSettings called:', {
      userId: user.attributes.sub,
      adminBusinessId,
      isAdminMode: !!adminBusinessId,
      WARNING: adminBusinessId ? 'Should use admin endpoint!' : 'OK'
    });
    setHasLoadedSettings(true);
    try {
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(adminBusinessId)
        : await configService.getBusinessConfigByUserId(user.attributes.sub);
      
      console.log('[VoiceSelection] Loaded config:', {
        business_name: config?.business_name,
        voice_id: config?.voice_settings?.voice_id,
        usedAdminEndpoint: !!adminBusinessId
      });
      
      let voiceFound = false;
      
      if (config) {
        // Load existing voice settings
        if (config.voice_settings && Object.keys(voices).length > 0) {
          // Find which gender category contains this voice_id
          const voiceId = config.voice_settings.voice_id;
          for (const [gender, voiceList] of Object.entries(voices)) {
            const matchingVoice = voiceList.find(voice => voice.voice_id === voiceId);
            if (matchingVoice) {
              setSelectedGender(gender);
              setSelectedVoice(matchingVoice);
              // Cache in localStorage for next page load
              localStorage.setItem('selectedGender', gender);
              localStorage.setItem('selectedVoice', JSON.stringify(matchingVoice));
              voiceFound = true;
              break;
            }
          }
        }
        
        // Load existing personality traits
        if (config.formality || config.verbosity || config.empathy || config.proactivity || config.humor) {
          setPersonalityTraits({
            formality: config.formality || 'Business Casual',
            verbosity: config.verbosity || 'Balanced',
            empathy: config.empathy || 'Warm and Understanding',
            proactivity: config.proactivity || 'Suggestive',
            humor: config.humor || 'Completely Serious'
          });
        }
      }
      
      // Set defaults if no existing voice configuration found
      if (!voiceFound && Object.keys(voices).length > 0) {
        const defaultGender = 'female';
        if (voices[defaultGender]?.length > 0) {
          const defaultVoice = voices[defaultGender][0];
          setSelectedGender(defaultGender);
          setSelectedVoice(defaultVoice);
          localStorage.setItem('selectedGender', defaultGender);
          localStorage.setItem('selectedVoice', JSON.stringify(defaultVoice));
        }
      }
    } catch (error) {
      console.warn('Could not load existing voice settings:', error);
      // Set defaults on error as well
      if (Object.keys(voices).length > 0) {
        const defaultGender = 'female';
        if (voices[defaultGender]?.length > 0) {
          const defaultVoice = voices[defaultGender][0];
          setSelectedGender(defaultGender);
          setSelectedVoice(defaultVoice);
          localStorage.setItem('selectedGender', defaultGender);
          localStorage.setItem('selectedVoice', JSON.stringify(defaultVoice));
        }
      }
    } finally {
      setSettingsLoaded(true);
    }
  };

  const getAccessToken = () => {
    // Find Cognito access token in localStorage
    for (const key of Object.keys(localStorage)) {
      if (key.includes('accessToken') && key.includes('CognitoIdentityServiceProvider')) {
        const token = localStorage.getItem(key);
        if (token && token.split('.').length === 3) {
          return token;
        }
      }
    }
    return null;
  };

  const initializeStreaming = async () => {
    try {
      if (!user) {
        setStreamingEnabled(false);
        return;
      }
      
      const token = getAccessToken();
      
      if (token) {
        voiceStreamingService.setAuthToken(token);
        await voiceStreamingService.connect(token);
        setStreamingEnabled(true);
      } else {
        setStreamingEnabled(false);
      }
    } catch (error) {
      setStreamingEnabled(false);
    }
  };

  useEffect(() => {
    if (hasLoadedSettings) {
      return;
    }
    if (Object.keys(voices).length > 0 && user && !cachedBusinessConfig) {
      loadExistingSettings();
    } else if (Object.keys(voices).length > 0 && cachedBusinessConfig) {
      loadExistingSettingsFromCache(cachedBusinessConfig);
    }
  }, [voices, user, cachedBusinessConfig]);

  const loadAvailableVoices = async () => {
    try {
      setLoading(true);
      const data = await configService.getAvailableVoices();

      if (data.success) {
        setVoices(data.voices);
        
        // Don't auto-select - let loadExistingSettings handle selection
      } else {
        setError(data.error || 'Failed to load voices');
      }
    } catch (err) {
      setError(err.message || 'Failed to load available voices');
      console.error('Voice loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenderChange = async (gender) => {
    setSelectedGender(gender);
    if (voices[gender]?.length > 0) {
      const firstVoice = voices[gender][0];
      setSelectedVoice(firstVoice);
      try {
        await configService.saveVoiceSettings(firstVoice);
        const config = await configService.getBusinessConfigByUserId();
        updateCache('settings', { businessConfig: config });
        showToast('Saved successfully!');
      } catch (error) {
        showToast('Failed to save', 'error');
      }
    }
  };

  const handleVoiceChange = async (voice, skipSave = false) => {
    setSelectedVoice(voice);
    if (!skipSave) {
      try {
        await configService.saveVoiceSettings(voice);
        const config = await configService.getBusinessConfigByUserId();
        updateCache('settings', { businessConfig: config });
        showToast('Saved successfully!');
      } catch (error) {
        showToast('Failed to save', 'error');
      }
    }
  };

  const previewVoice = async (voice) => {
    if (isPlaying) return;
    setPreviewingVoice(voice.voice_id);
    setIsPlaying(true);
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        voiceStreamingService.setAuthToken(token);
        await voiceStreamingService.connect(token);
      }

      const voiceConfig = {
        provider: voice.provider,
        voice_id: voice.voice_id,
        provider_config: voice.provider_config
      };
      
      const response = await configService.generatePersonalityResponse(selectedScenario, voiceConfig);
      
      if (response.status !== 'error' && response.response) {
        await voiceStreamingService.streamVoicePreview(
          voice.provider,
          voice.provider_config,
          response.response
        );
      }
    } catch (error) {
      console.error('Preview failed:', error);
      showToast('Preview failed', 'error');
    } finally {
      setIsPlaying(false);
      setPreviewingVoice(null);
    }
  };

  const stopPreview = () => {
    voiceStreamingService.disconnect();
    setIsPlaying(false);
    setPreviewingVoice(null);
  };

  // Extract all unique attributes from voices, with gender attributes first
  const getAvailableAttributes = () => {
    const genderAttributes = [];
    const otherAttributes = new Set();
    const currentGender = getCurrentGender();
    
    // Add gender options
    ['female', 'male'].forEach(gender => {
      if (voices[gender]?.length > 0) {
        genderAttributes.push(gender === 'female' ? 'feminine' : 'masculine');
      }
    });
    
    // Only collect attributes from the currently selected gender
    if (voices[currentGender]) {
      voices[currentGender].forEach(voice => {
        if (voice.style) {
          voice.style.split(',').forEach(attr => {
            const trimmed = attr.trim();
            if (trimmed !== 'feminine' && trimmed !== 'masculine') {
              otherAttributes.add(trimmed);
            }
          });
        }
      });
    }
    
    return [...genderAttributes, ...Array.from(otherAttributes).sort()];
  };

  // Get current gender based on selected filters
  const getCurrentGender = () => {
    if (selectedFilters.includes('feminine')) return 'female';
    if (selectedFilters.includes('masculine')) return 'male';
    return selectedGender;
  };

  // Filter voices based on selected attributes
  const getFilteredVoices = () => {
    const gender = getCurrentGender();
    const nonGenderFilters = selectedFilters.filter(f => f !== 'feminine' && f !== 'masculine');
    
    if (nonGenderFilters.length === 0) return voices[gender] || [];
    
    return (voices[gender] || []).filter(voice => {
      if (!voice.style) return false;
      const voiceAttributes = voice.style.split(',').map(a => a.trim());
      return nonGenderFilters.every(filter => voiceAttributes.includes(filter));
    });
  };

  const toggleFilter = (attribute) => {
    // Handle gender filters (radio button behavior)
    if (attribute === 'feminine' || attribute === 'masculine') {
      const newGender = attribute === 'feminine' ? 'female' : 'male';
      setSelectedGender(newGender);
      // Clear non-gender filters when switching gender
      setSelectedFilters([attribute]);
    } else {
      // Handle other filters (checkbox behavior)
      setSelectedFilters(prev => 
        prev.includes(attribute)
          ? prev.filter(f => f !== attribute)
          : [...prev, attribute]
      );
    }
  };

  // Initialize gender filter on mount
  useEffect(() => {
    if (selectedFilters.length === 0) {
      setSelectedFilters([selectedGender === 'female' ? 'feminine' : 'masculine']);
    }
  }, []);



  const saveVoiceSelection = async () => {
    if (!selectedVoice) {
      setError('Please select a voice first');
      return;
    }

    try {
      // Save voice settings using business_id-based API
      const voiceData = await configService.saveVoiceSettings(selectedVoice);

      if (!voiceData.message && !voiceData.success) {
        setError(voiceData.error || 'Failed to save voice settings');
        return;
      }

      // Save personality traits using business_id-based API
      const personalityData = await configService.updatePersonalityTraits(
        personalityTraits
      );

      if (personalityData.success || personalityData.message) {
        onVoiceSelected?.(selectedVoice);
      } else {
        setError('Voice saved but personality traits failed to save');
      }
    } catch (err) {
      setError(err.message || 'Failed to save settings');
      console.error('Save error:', err);
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Design System
  const designSystem = {
    colors: {
      primary: 'rgb(5, 94, 253)',
      background: 'rgb(255, 255, 255)',
      lightBackground: 'rgb(249, 249, 251)',
      text: 'rgb(34, 35, 36)',
      textSecondary: 'rgb(107, 114, 126)',
      border: 'rgb(229, 231, 235)',
      error: 'rgb(239, 68, 68)'
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      fontWeights: { medium: 500, semibold: 600, bold: 700 }
    },
    borderRadius: '4px',
    spacing: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' }
  };

  if (loading || !settingsLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: designSystem.spacing.lg }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: `3px solid ${designSystem.colors.border}`,
          borderTop: `3px solid ${designSystem.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: `0 auto ${designSystem.spacing.sm}`
        }}></div>
        <p style={{
          fontFamily: designSystem.typography.fontFamily,
          color: designSystem.colors.textSecondary,
          fontSize: '14px',
          margin: 0
        }}>Loading voices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'rgb(254, 242, 242)',
        padding: designSystem.spacing.lg,
        borderRadius: designSystem.borderRadius,
        marginBottom: designSystem.spacing.lg
      }}>
        <p style={{
          fontFamily: designSystem.typography.fontFamily,
          color: designSystem.colors.error,
          marginBottom: designSystem.spacing.sm,
          fontSize: '14px'
        }}>{error}</p>
        <button
          onClick={() => { setError(null); loadAvailableVoices(); }}
          style={{
            backgroundColor: designSystem.colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: designSystem.borderRadius,
            padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
            fontFamily: designSystem.typography.fontFamily,
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.95); }
        }
      `}</style>
      {/* Personality Traits - Moved to top */}
      <PersonalityTraits 
        initialTraits={personalityTraits}
        onTraitsChange={(newTraits) => {
          setPersonalityTraits(newTraits);
          updateCache('settings', { businessConfig: { ...cachedBusinessConfig, ...newTraits } });
        }}
        hidePreview={true}
      />

      {/* Current Voice Selection Card */}
      {selectedVoice && (
        <div style={{
          backgroundColor: designSystem.colors.lightBackground,
          padding: designSystem.spacing.lg,
          borderRadius: designSystem.borderRadius,
          marginBottom: designSystem.spacing.xl,
          marginTop: designSystem.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: designSystem.spacing.sm
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: designSystem.spacing.sm,
                fontFamily: designSystem.typography.fontFamily,
                fontWeight: designSystem.typography.fontWeights.medium,
                color: designSystem.colors.text,
                fontSize: '14px'
              }}>Current Voice Selection</label>
              <div style={{
                fontFamily: designSystem.typography.fontFamily,
                fontSize: '18px',
                fontWeight: designSystem.typography.fontWeights.semibold,
                color: designSystem.colors.text,
                marginBottom: designSystem.spacing.xs
              }}>
                {selectedVoice.display_name}
              </div>
              {selectedVoice.style && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginTop: designSystem.spacing.sm
                }}>
                  {selectedVoice.style.split(',').map((trait, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        backgroundColor: 'rgba(107, 114, 126, 0.1)',
                        color: designSystem.colors.textSecondary,
                        borderRadius: '3px',
                        fontFamily: designSystem.typography.fontFamily
                      }}
                    >
                      {trait.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowVoiceOptions(!showVoiceOptions)}
            style={{
              marginTop: designSystem.spacing.md,
              padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
              backgroundColor: designSystem.colors.background,
              border: `1px solid ${designSystem.colors.border}`,
              borderRadius: designSystem.borderRadius,
              fontFamily: designSystem.typography.fontFamily,
              fontSize: '13px',
              fontWeight: designSystem.typography.fontWeights.medium,
              color: designSystem.colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: designSystem.spacing.sm
            }}
          >
            <i className={`fa fa-chevron-${showVoiceOptions ? 'up' : 'down'}`}></i>
            {showVoiceOptions ? 'Hide' : 'Change'} Voice Options
          </button>
        </div>
      )}

      {/* Voice Selection Options - Collapsible */}
      {showVoiceOptions && (
        <>
      <div style={{ marginBottom: designSystem.spacing.xl, marginTop: designSystem.spacing.xl }}>
        <label style={{
          display: 'block',
          marginBottom: designSystem.spacing.md,
          fontFamily: designSystem.typography.fontFamily,
          fontWeight: designSystem.typography.fontWeights.medium,
          color: designSystem.colors.text,
          fontSize: '14px'
        }}>Preview Scenario</label>
        <select
          value={selectedScenario}
          onChange={(e) => setSelectedScenario(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: designSystem.spacing.md,
            border: `1px solid ${designSystem.colors.border}`,
            borderRadius: designSystem.borderRadius,
            fontSize: '16px',
            fontFamily: designSystem.typography.fontFamily,
            color: designSystem.colors.text,
            backgroundColor: designSystem.colors.background,
            minHeight: '44px'
          }}
        >
          {scenarios.map(scenario => (
            <option key={scenario.key} value={scenario.key}>
              {scenario.label}
            </option>
          ))}
        </select>
      </div>

      {/* Attribute Filters (including gender as first filters) */}
      {getAvailableAttributes().length > 0 && (
        <div style={{ marginBottom: designSystem.spacing.xl }}>
          <label style={{
            display: 'block',
            marginBottom: designSystem.spacing.md,
            fontFamily: designSystem.typography.fontFamily,
            fontWeight: designSystem.typography.fontWeights.medium,
            color: designSystem.colors.text,
            fontSize: '14px'
          }}>Filter by Style</label>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: designSystem.spacing.sm
          }}>
            {getAvailableAttributes().map((attribute, idx) => {
              const isGenderFilter = attribute === 'feminine' || attribute === 'masculine';
              const isSelected = selectedFilters.includes(attribute);
              
              return (
                <button
                  key={attribute}
                  onClick={() => toggleFilter(attribute)}
                  style={{
                    padding: `${designSystem.spacing.sm} ${designSystem.spacing.md}`,
                    border: `2px solid ${isSelected ? designSystem.colors.primary : designSystem.colors.border}`,
                    borderRadius: designSystem.borderRadius,
                    backgroundColor: isSelected ? 'rgba(5, 94, 253, 0.1)' : designSystem.colors.background,
                    color: isSelected ? designSystem.colors.primary : designSystem.colors.text,
                    fontFamily: designSystem.typography.fontFamily,
                    fontSize: '13px',
                    fontWeight: isSelected ? designSystem.typography.fontWeights.semibold : designSystem.typography.fontWeights.medium,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: '36px',
                    marginRight: isGenderFilter && idx === 1 ? designSystem.spacing.md : '0'
                  }}
                >
                  {attribute}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Voice Selection - Cards with Preview Button */}
      {voices[selectedGender]?.length > 0 && (
        <div style={{ marginBottom: designSystem.spacing.xl }}>
          <label style={{
            display: 'block',
            marginBottom: designSystem.spacing.md,
            fontFamily: designSystem.typography.fontFamily,
            fontWeight: designSystem.typography.fontWeights.medium,
            color: designSystem.colors.text,
            fontSize: '14px'
          }}>Select Voice {selectedFilters.filter(f => f !== 'feminine' && f !== 'masculine').length > 0 && `(${getFilteredVoices().length} of ${voices[getCurrentGender()].length})`}</label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: designSystem.spacing.md
          }}>
            {getFilteredVoices().map(voice => {
              const isSelected = selectedVoice?.voice_id === voice.voice_id;
              return (
              <div
                key={voice.voice_id}
                style={{
                  position: 'relative',
                  padding: designSystem.spacing.md,
                  border: `2px solid ${isSelected ? designSystem.colors.primary : designSystem.colors.border}`,
                  borderRadius: designSystem.borderRadius,
                  backgroundColor: isSelected ? 'rgba(5, 94, 253, 0.05)' : designSystem.colors.background,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div 
                    onClick={() => handleVoiceChange(voice)}
                    style={{ flex: 1 }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: designSystem.spacing.xs,
                      marginBottom: designSystem.spacing.xs
                    }}>
                      <span style={{
                        fontFamily: designSystem.typography.fontFamily,
                        fontSize: '16px',
                        fontWeight: designSystem.typography.fontWeights.semibold,
                        color: designSystem.colors.text
                      }}>
                        {voice.display_name}
                      </span>
                      {isSelected && (
                        <span style={{
                          fontSize: '16px',
                          color: designSystem.colors.primary
                        }}>✓</span>
                      )}
                    </div>
                    {voice.style && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginTop: designSystem.spacing.xs
                      }}>
                        {voice.style.split(',').map((trait, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              backgroundColor: 'rgba(107, 114, 126, 0.1)',
                              color: designSystem.colors.textSecondary,
                              borderRadius: '3px',
                              fontFamily: designSystem.typography.fontFamily
                            }}
                          >
                            {trait.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (previewingVoice === voice.voice_id) {
                        stopPreview();
                      } else {
                        previewVoice(voice);
                      }
                    }}
                    disabled={isPlaying && previewingVoice !== voice.voice_id}
                    style={{
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      backgroundColor: previewingVoice === voice.voice_id ? 'rgb(239, 68, 68)' : designSystem.colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      fontFamily: designSystem.typography.fontFamily,
                      fontSize: '18px',
                      fontWeight: designSystem.typography.fontWeights.semibold,
                      cursor: (isPlaying && previewingVoice !== voice.voice_id) ? 'not-allowed' : 'pointer',
                      opacity: (isPlaying && previewingVoice !== voice.voice_id) ? 0.5 : 1,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: previewingVoice === voice.voice_id ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }}
                  >
                    {previewingVoice === voice.voice_id ? '■' : '▶'}
                  </button>
                </div>
              </div>
            )})}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default VoiceSelection;